import React, { useEffect, useMemo, useRef, useState } from "react";
import { marked } from "marked";
import ForceGraph2D from "react-force-graph-2d";
import { AnimatePresence, motion } from "framer-motion";
import graphDefinition from "./data/nodes.json";
import appDemoView from "./data/img/app_demo_view.png";
import appDemoViewWabiSabi from "./data/img/app_demo_view_wabisabi.png";

/**
 * ONONO Graph Demo – Canvas-ready Single File (React + Tailwind)
 * Updates:
 * 1) Sidebar width is now 70% of the original (col-span-4 → col-span-3).
 * 2) Step 2 node (“Difference”) sits directly below the center at half the previous distance (80).
 * 3) Step 3 node (“What are mainstream AIs doing?”) sits below it at half that distance (40).
 * 4) Added a step that auto-selects the mainstream node and highlights it in the graph + sidebar.
 * 5) “Expand three subquestions” becomes two steps:
 *    5.1 Add “What does ONONO include?” to the left of the center node.
 *    5.2 Show ONONO ENGINE / ONONO APP / ONONO PERCEPT as its children.
 */

// —— Colors (dark theme) ——
const COLORS = {
  bg: "#0b0f15",
  panel: "#0f172a",
  text: "#e5e7eb",
  subtext: "#a5b4fc",
  border: "#1f2937",
  dotIdleBorder: "#475569",
  dotActiveFill: "#e5e7eb",

  // Nodes
  rootFill: "#22d3ee", // Center node accent color: cyan
  rootGlow: "#06b6d4",
  question: "#60a5fa", // Light blue
  concept: "#34d399", // Green
  answer: "#c084fc", // Purple
  source: "#fbbf24", // Orange
  viewpoint: "#f87171", // Red
  person: "#9ca3af", // Graphite
  "software component": "#2dd4bf", // Teal
  "software feature": "#f472b6", // Pink
  "internal tool": "#fcd34d", // Gold

  // Edges
  edge: "#94a3b8",
  edgeChild: "#93c5fd",
  edgeDetail: "#fbbf24",
  edgeRelated: "#94a3b8",

  // Highlights
  focusStroke: "#ffffff",
  focusGlow: "rgba(255,255,255,0.35)",
};

const linkRenderer = new marked.Renderer();
const defaultLinkRenderer = linkRenderer.link;
linkRenderer.link = function link(href, title, text) {
  const html = typeof defaultLinkRenderer === "function"
    ? defaultLinkRenderer.call(this, href, title, text)
    : "";
  if (typeof html !== "string") return html;
  if (html.includes("target=")) return html;
  return html.replace("<a ", '<a target="_blank" rel="noreferrer noopener" ');
};
marked.use({ renderer: linkRenderer });
marked.setOptions({ breaks: true, headerIds: false, mangle: false });

const DATA_FILE_PATH = "src/data/nodes.json";
const DESKTOP_BREAKPOINT = 768;
const SIDEBAR_DESKTOP_RATIO = 3 / 12; // matches md:grid-cols-12 with 3-column sidebar

const NODE_MAP = graphDefinition.nodes || {};
const SIDEBAR_ASSET_MAP = {
  "src/data/img/app_demo_view.png": appDemoView,
  "src/data/img/app_demo_view_wabisabi.png": appDemoViewWabiSabi,
};
const replaceSidebarAssets = (text = "") =>
  Object.entries(SIDEBAR_ASSET_MAP).reduce(
    (acc, [needle, assetPath]) => acc.split(needle).join(assetPath),
    text
  );

const normalizeSidebarMarkdown = (sidebar) => {
  if (typeof sidebar === "string") return sidebar;
  if (Array.isArray(sidebar)) {
    return sidebar
      .map((entry) => {
        if (typeof entry === "string") return entry.trim();
        const text = entry?.text ?? "";
        if (!text) return "";
        return entry.emphasis ? `**${text}**` : text;
      })
      .filter(Boolean)
      .map((line) => `- ${line}`)
      .join("\n");
  }
  return "";
};

const cloneNode = (id) => {
  const def = NODE_MAP[id];
  if (!def) return null;
  const { sidebar, ...rest } = def;
  const normalizedSidebar = normalizeSidebarMarkdown(sidebar);
  return {
    id,
    ...rest,
    sidebar: replaceSidebarAssets(normalizedSidebar),
  };
};

const resolvedRoot = cloneNode(graphDefinition.rootId);
if (!resolvedRoot) {
  // eslint-disable-next-line no-console
  console.warn(`Root node ${graphDefinition.rootId} missing in ${DATA_FILE_PATH}`);
}

const ROOT_NODE = resolvedRoot ?? {
  id: "q-onono",
  label: "What is ONONO?",
  kind: "question",
  level: 0,
  fx: 0,
  fy: 0,
  sidebar: "",
};

const buildExpansions = (expansions = {}) =>
  Object.entries(expansions).reduce((acc, [key, pack]) => {
    acc[key] = {
      nodes: (pack.nodes || []).map(cloneNode).filter(Boolean),
      links: (pack.links || []).map((link) => ({ ...link })),
    };
    return acc;
  }, {});

const EXPANSIONS = buildExpansions(graphDefinition.expansions);
const RELATION_LABEL_TARGETS = new Set([
  "q-app",
  "c-know",
  "q-why-know",
  "q-human-vs-ai",
  "c-intel",
  "c-context-field",
  "c-embodied-signal",
  "c-ethical-anchor",
]);
const RELATION_LABEL_STEP_KEYS = new Set(["S6", "S7"]);
const HIDE_NODE_LABEL_STEP_KEYS = new Set(["S8"]);
const RELATION_ACCENT_COLORS = {
  component: "#2dd4bf",
  feature: "#f472b6",
  tool: "#fcd34d",
  "owner/product manager": "#38bdf8",
  dimension: "#fbbf24",
  shapes: "#34d399",
  frames: "#a78bfa",
  anchors: "#fb7185",
  grounds: "#60a5fa",
  questions: "#f97316",
  aligns: "#22d3ee",
};

const stepTimeouts = { current: [] };
const clearScheduledTimeouts = () => {
  stepTimeouts.current.forEach((id) => clearTimeout(id));
  stepTimeouts.current = [];
};
const scheduleStepTimeout = (fn, delay = 0) => {
  const id = setTimeout(() => {
    stepTimeouts.current = stepTimeouts.current.filter((tid) => tid !== id);
    fn();
  }, delay);
  stepTimeouts.current.push(id);
  return id;
};

// Utility helpers
const uniqPush = (arr, items, key = "id") => {
  const exist = new Set(arr.map((d) => d[key] ?? `${d.source}->${d.target}`));
  items.forEach((it) => {
    const k = it[key] ?? `${it.source}->${it.target}`;
    if (!exist.has(k)) arr.push(it);
  });
};

export default function OnonoGraphDemo() {
  const fgRef = useRef();
  const menuRef = useRef(null);
  const [graphData, setGraphData] = useState({ nodes: [{ ...ROOT_NODE }], links: [] });
  const [presentMode, setPresentMode] = useState(false);
  const [focused, setFocused] = useState(null);
  const [pendingFocus, setPendingFocus] = useState(null);
  const [finalOverlay, setFinalOverlay] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [showSidebar, setShowSidebar] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileSidebarTab, setMobileSidebarTab] = useState("content");
  const [imageViewer, setImageViewer] = useState({ open: false, src: "", alt: "" });
  const [imageZoom, setImageZoom] = useState(1);
  const setFocusedNode = (node) => {
    setFocused(node);
    setShowSidebar(Boolean(node));
  };
  const sidebarVisible = showSidebar && Boolean(focused);

  const findNode = (id) => graphData.nodes.find((n) => n.id === id);

  const setGraph = (mut) => {
    setGraphData((g) => {
      const nodes = g.nodes.map((d) => ({ ...d }));
      const links = g.links.map((d) => ({ ...d }));
      const next = { nodes, links };
      mut(next);
      return next;
    });
  };

  const addExpansion = (key) => {
    const pack = EXPANSIONS[key];
    if (!pack) return;
    setGraph((g) => {
      uniqPush(g.nodes, pack.nodes, "id");
      uniqPush(g.links, pack.links, undefined);
    });
  };

  const keepOnly = (ids) => {
    setGraph((g) => {
      const keep = new Set(ids);
      const nodes = g.nodes.filter((n) => keep.has(n.id));
      const nodeSet = new Set(nodes.map((n) => n.id));
      const links = g.links.filter(
        (l) => nodeSet.has(l.source.id ?? l.source) && nodeSet.has(l.target.id ?? l.target)
      );
      g.nodes = nodes; g.links = links;
    });
  };

  const collapseTo = (keepIds = ["q-onono"]) => {
    setGraph((g) => {
      const keep = new Set(keepIds.concat(["q-onono"]));
      const nodes = g.nodes.filter((n) => keep.has(n.id));
      const nodeSet = new Set(nodes.map((n) => n.id));
      const links = g.links.filter(
        (l) => nodeSet.has(l.source.id ?? l.source) && nodeSet.has(l.target.id ?? l.target)
      );
      g.nodes = nodes;
      g.links = links;
    });
  };

// Set coordinates with optional fx/fy lock
  const setNodePos = (id, x, y, fixed = false) => {
    setGraph((g) => {
      const n = g.nodes.find((d) => d.id === id);
      if (n) {
        n.x = x; n.y = y;
        if (fixed) { n.fx = x; n.fy = y; }
      }
    });
  };
  const releaseNode = (id) => {
    setGraph((g) => {
      const n = g.nodes.find((d) => d.id === id);
      if (n) { n.fx = undefined; n.fy = undefined; }
    });
  };

  const zoomCenter = (k = 1.6) => {
    const api = fgRef.current; if (!api) return;
    api.zoom(k, 700); api.centerAt(0, 0, 700);
  };

  const getCanvasSize = () => {
    const api = fgRef.current;
    const fallbackWidth = typeof window !== "undefined" ? window.innerWidth : 0;
    const fallbackHeight = typeof window !== "undefined" ? window.innerHeight : 0;
    if (!api) return { width: fallbackWidth, height: fallbackHeight };
    const widthValue = typeof api.width === "function" ? api.width() : undefined;
    const heightValue = typeof api.height === "function" ? api.height() : undefined;
    return {
      width: typeof widthValue === "number" ? widthValue : fallbackWidth,
      height: typeof heightValue === "number" ? heightValue : fallbackHeight,
    };
  };

  const getPositionedNodes = () => {
    const api = fgRef.current;
    const sourceNodes =
      (api && typeof api.graphData === "function" ? api.graphData().nodes : graphData.nodes) || [];
    return sourceNodes.filter(
      (node) => typeof node?.x === "number" && typeof node?.y === "number"
    );
  };

  const getGraphBounds = () => {
    const nodes = getPositionedNodes();
    if (!nodes.length) return null;
    let minX = Infinity; let maxX = -Infinity;
    let minY = Infinity; let maxY = -Infinity;
    nodes.forEach((node) => {
      minX = Math.min(minX, node.x);
      maxX = Math.max(maxX, node.x);
      minY = Math.min(minY, node.y);
      maxY = Math.max(maxY, node.y);
    });
    return { minX, maxX, minY, maxY };
  };

  const desktopSidebarActive = () => {
    if (!sidebarVisible) return false;
    const { width } = getCanvasSize();
    return width >= DESKTOP_BREAKPOINT;
  };

  const zoomToFit = (options = {}) => {
    const respectSidebar = options.respectSidebar !== undefined ? options.respectSidebar : true;
    const zoomOutFactor = options.zoomOutFactor ?? 0.8;
    const minZoom = options.minZoom ?? 0.2;
    const maxZoom = options.maxZoom ?? 12;
    const api = fgRef.current; if (!api) return;
    const bounds = getGraphBounds();
    if (!bounds) {
      if (typeof api.zoomToFit === "function") api.zoomToFit(900, 80, () => true);
      return;
    }
    const { width, height } = getCanvasSize();
    if (!width || !height) {
      if (typeof api.zoomToFit === "function") api.zoomToFit(900, 80, () => true);
      return;
    }
    const sidebarActive = respectSidebar && desktopSidebarActive();
    const sidebarWidthPx = sidebarActive ? width * SIDEBAR_DESKTOP_RATIO : 0;
    const horizontalPadding = 80;
    const verticalPadding = 80;
    const usableWidth = Math.max(width - sidebarWidthPx - horizontalPadding * 2, 50);
    const usableHeight = Math.max(height - verticalPadding * 2, 50);
    if (usableWidth <= 0 || usableHeight <= 0) {
      if (typeof api.zoomToFit === "function") api.zoomToFit(900, 80, () => true);
      return;
    }
    const spanX = Math.max(bounds.maxX - bounds.minX, 1);
    const spanY = Math.max(bounds.maxY - bounds.minY, 1);
    const zoomX = usableWidth / spanX;
    const zoomY = usableHeight / spanY;
    const targetZoom = Math.min(zoomX, zoomY);
    const clampedZoom = Math.max(Math.min(targetZoom, maxZoom), minZoom);
    const finalZoom = Math.max(clampedZoom * zoomOutFactor, minZoom);
    const duration = 900;
    api.zoom(finalZoom, duration);
    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerY = (bounds.minY + bounds.maxY) / 2;
    const horizontalShift = sidebarActive ? (sidebarWidthPx / 2) / finalZoom : 0;
    api.centerAt(centerX + horizontalShift, centerY, duration);
  };
  const focusOn = (id, options = {}) => {
    if (!id) return;
    const duration = options.duration ?? 800;
    const zoom = options.zoom ?? 2;
    const node = findNode(id);
    if (!node) {
      setPendingFocus({ id, duration, zoom });
      return;
    }
    setPendingFocus(null);
    setFocusedNode(node);
    const api = fgRef.current;
    if (api) {
      api.centerAt(node.x ?? 0, node.y ?? 0, duration);
      api.zoom(zoom, duration);
    }
  };

  // —— Step definitions (insert/adjust) ——
  const STEPS = [
    { key: "S0", label: "1. Central question", apply: () => { } },
    {
      key: "S1a", label: "2. ‘Difference’ below center", apply: () => {
        addExpansion("q-onono");
        keepOnly(["q-onono", "q-diff"]);
        // Place it directly below center, distance 80 (half of original 160)
        setNodePos("q-diff", 0, 80, true);
        setFocusedNode(null);
      }
    },
    {
      key: "S1b", label: "3. ‘Mainstream’ below it", apply: () => {
        addExpansion("q-diff");
        const pd = findNode("q-diff");
        const x = (pd?.x ?? 0);
        const y = (pd?.y ?? 80) + 40;
        setNodePos("q-mainstream", x, y, true);
        setFocusedNode(null);
      }
    },
    {
      key: "S1c", label: "4. Auto-select ‘Mainstream AI’", apply: () => {
        focusOn("q-mainstream");
      }
    },
    {
      key: "S2a", label: "5. New node: What does ONONO include?", apply: () => {
        addExpansion("q-onono-what-parent");
        setNodePos("q-what", -140, 0, true);
        focusOn("q-what");
      }
    },
    {
      key: "S2b", label: "6. Children: APP / ENGINE / PERCEPT", apply: () => {
        addExpansion("q-what");
        // Three child nodes further left, vertically distributed
        setNodePos("q-app", -260, -40, true);
        setNodePos("q-engine", -260, 0, true);
        setNodePos("q-percept", -260, 40, true);
        focusOn("q-app");
      }
    },
    { key: "S4", label: "7. Focus ENGINE", apply: () => focusOn("q-engine") },
    { key: "S5", label: "8. Focus PERCEPT", apply: () => focusOn("q-percept") },
    {
      key: "S6", label: "9. Expand APP details", apply: () => {
        addExpansion("q-app");
        const appNode = findNode("q-app");
        const baseX = appNode?.x ?? -260;
        const baseY = appNode?.y ?? -40;
        const fanLayout = [
          { id: "comp-onono-bridge", offset: -0.95, radius: 205 },
          { id: "comp-ledgers", offset: -0.45, radius: 185 },
          { id: "person-kaiyue", offset: 0, radius: 175 },
          { id: "feat-adaptive-interface", offset: 0.45, radius: 185 },
          { id: "feat-multi-view-timeline", offset: 0.95, radius: 205 },
          { id: "tool-design-archive", offset: 1.3, radius: 225 },
        ];
        const verticalLift = 20;
        const baseAngle = Math.PI; // aim left
        fanLayout.forEach(({ id, offset, radius }) => {
          const angle = baseAngle + offset;
          const x = baseX + Math.cos(angle) * radius;
          const y = baseY + Math.sin(angle) * radius - verticalLift;
          setNodePos(id, x, y, true);
        });
        setFocusedNode(null);
        setShowSidebar(false);
        const { width, height } = getCanvasSize();
        const isDesktop = width >= DESKTOP_BREAKPOINT;
        const api = fgRef.current;
        if (!api) return;
        if (!appNode) {
          zoomToFit({ respectSidebar: false, zoomOutFactor: isDesktop ? 0.55 : 0.65, minZoom: 0.42 });
          return;
        }
        const duration = 900;
        if (isDesktop) {
          const bounds = getGraphBounds();
          if (!bounds || !width || !height) {
            zoomToFit({ respectSidebar: false, zoomOutFactor: 0.55, minZoom: 0.42 });
            return;
          }
          const horizontalPadding = 80;
          const verticalPadding = 80;
          const usableWidth = Math.max(width - horizontalPadding * 2, 50);
          const usableHeight = Math.max(height - verticalPadding * 2, 50);
          if (usableWidth <= 0 || usableHeight <= 0) {
            zoomToFit({ respectSidebar: false, zoomOutFactor: 0.55, minZoom: 0.42 });
            return;
          }
          const spanX = Math.max(bounds.maxX - bounds.minX, 1);
          const spanY = Math.max(bounds.maxY - bounds.minY, 1);
          const zoomX = usableWidth / spanX;
          const zoomY = usableHeight / spanY;
          const targetZoom = Math.min(zoomX, zoomY);
          const minZoom = 0.35;
          const maxZoom = 12;
          const clampedZoom = Math.max(Math.min(targetZoom, maxZoom), minZoom);
          const zoomFactor = 0.44; // 20% more zoom out than previous 0.55
          const finalZoom = Math.max(clampedZoom * zoomFactor, minZoom);
          api.zoom(finalZoom, duration);
          api.centerAt(appNode.x ?? baseX, (appNode.y ?? baseY) - 10, duration);
        } else {
          api.centerAt(appNode.x ?? baseX, (appNode.y ?? baseY) - 10, duration);
          api.zoom(1.2, duration);
        }
      }
    },
    {
      key: "S7", label: "10. Concepts & human strengths", apply: () => {
        const rootNode = findNode("q-onono");
        const baseX = rootNode?.x ?? 0;
        const baseY = rootNode?.y ?? 0;
        addExpansion("super-of-q-onono");
        addExpansion("knowledge-dimensions");
        addExpansion("c-know");
        addExpansion("knowledge-dimension-bridges");
        addExpansion("q-human-vs-ai");
        setNodePos("c-ai", baseX - 340, baseY - 60, true);
        setNodePos("c-intel", baseX - 80, baseY - 260, true);
        setNodePos("c-know", baseX + 140, baseY - 210, true);
        setNodePos("c-under", baseX + 360, baseY - 40, true);
        setNodePos("c-context-field", baseX - 440, baseY - 210, true);
        setNodePos("c-embodied-signal", baseX + 440, baseY - 210, true);
        setNodePos("c-ethical-anchor", baseX + 60, baseY - 430, true);
        setNodePos("q-why-know", baseX - 280, baseY - 400, true);
        setNodePos("q-human-vs-ai", baseX + 280, baseY - 400, true);
        setNodePos("a-human-1", baseX + 330, baseY - 490, true);
        setNodePos("a-human-2", baseX + 380, baseY - 420, true);
        setFocusedNode(null);
        setShowSidebar(false);
        zoomToFit({ respectSidebar: false, zoomOutFactor: 0.78, minZoom: 0.55 });
      }
    },
    {
      key: "S8", label: "11. Full view + tagline", apply: () => {
        const { width } = getCanvasSize();
        const isDesktop = width >= DESKTOP_BREAKPOINT;
        const zoomFactor = isDesktop ? 0.3 : 0.45;
        const minZoom = isDesktop ? 0.15 : 0.22;
        setFocusedNode(null);
        setShowSidebar(false);
        setFinalOverlay(false);
        zoomToFit({ respectSidebar: false, zoomOutFactor: zoomFactor, minZoom });
        scheduleStepTimeout(() => { setFinalOverlay(true); }, 900);
      }
    },
  ];
  const maxStep = STEPS.length - 1;
  const totalSteps = STEPS.length;
  const currentStep = stepIndex + 1;
  const currentStepKey = STEPS[stepIndex]?.key;
  const graphCols = sidebarVisible ? "md:col-span-9" : "md:col-span-12";
  const relationEntries = useMemo(() => {
    if (!focused) return [];
    const nodeId = focused.id;
    if (!nodeId) return [];
    const nodesById = new Map(graphData.nodes.map((n) => [n.id, n]));
    const relations = [];
    const seen = new Set();
    const nodeIdFromRef = (ref) => (typeof ref === "object" && ref !== null ? ref.id : ref);
    graphData.links.forEach((link) => {
      const sourceId = nodeIdFromRef(link.source);
      const targetId = nodeIdFromRef(link.target);
      if (sourceId !== nodeId && targetId !== nodeId) return;
      const neighborId = sourceId === nodeId ? targetId : sourceId;
      if (!neighborId) return;
      const neighborNode = nodesById.get(neighborId);
      if (!neighborNode) return;
      const direction = sourceId === nodeId ? "outgoing" : "incoming";
      const type = link.type || "related";
      const key = `${neighborId}-${direction}-${type}`;
      if (seen.has(key)) return;
      seen.add(key);
      relations.push({
        id: neighborNode.id,
        label: neighborNode.label || neighborNode.id,
        type,
        direction,
      });
    });
    return relations.sort((a, b) => {
      if (a.direction === b.direction) return a.label.localeCompare(b.label);
      return a.direction === "outgoing" ? -1 : 1;
    });
  }, [focused, graphData]);
  const hideNodeLabels = currentStepKey ? HIDE_NODE_LABEL_STEP_KEYS.has(currentStepKey) : false;
  const showRelationLabels =
    currentStepKey ? (RELATION_LABEL_STEP_KEYS.has(currentStepKey) && !hideNodeLabels) : false;
  const handleCloseSidebar = () => { setFocusedNode(null); };
  const toggleMenu = () => setMenuOpen((v) => !v);
  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    const updateViewportHeight = () => {
      if (typeof window === "undefined" || typeof document === "undefined") return;
      document.documentElement.style.setProperty("--app-viewport-height", `${window.innerHeight}px`);
    };
    updateViewportHeight();
    window.addEventListener("resize", updateViewportHeight);
    window.addEventListener("orientationchange", updateViewportHeight);
    return () => {
      window.removeEventListener("resize", updateViewportHeight);
      window.removeEventListener("orientationchange", updateViewportHeight);
    };
  }, []);

  // —— Recalculate state ——
  const recomputeTo = (idx) => {
    clearScheduledTimeouts();
    setFinalOverlay(false); setFocusedNode(null);
    setGraphData({ nodes: [{ ...ROOT_NODE }], links: [] });
    requestAnimationFrame(() => { for (let i = 0; i <= idx; i++) STEPS[i]?.apply?.(); });
  };

  // Initial center
  useEffect(() => { zoomCenter(1.35); }, []);

  // Keyboard
  useEffect(() => {
    const onKey = (e) => {
      const key = e.key.toLowerCase();
      if (key === "arrowright") { e.preventDefault(); nextStep(); }
      if (key === "arrowleft") { e.preventDefault(); prevStep(); }
      if (key === "f") { e.preventDefault(); zoomToFit(); }
      if (key === "g") { e.preventDefault(); zoomToFit({ respectSidebar: false }); }
      if (key === "m") setPresentMode((v) => !v);
      if (key === "r") goStep(0);
      if (key === "s") setShowSidebar((v) => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [stepIndex, graphData]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) closeMenu();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  useEffect(() => {
    setMobileSidebarTab("content");
  }, [focused?.id]);

  useEffect(() => {
    if (!pendingFocus) return;
    const { id, duration, zoom } = pendingFocus;
    const target = findNode(id);
    if (!target) return;
    setPendingFocus(null);
    setFocusedNode(target);
    const api = fgRef.current;
    if (api) {
      api.centerAt(target.x ?? 0, target.y ?? 0, duration);
      api.zoom(zoom, duration);
    }
  }, [pendingFocus, graphData]);

  useEffect(() => () => clearScheduledTimeouts(), []);

  // Step switching
  const goStep = (idx) => { const c = Math.max(0, Math.min(idx, maxStep)); setStepIndex(c); recomputeTo(c); };
  const nextStep = () => goStep(stepIndex + 1);
  const prevStep = () => goStep(stepIndex - 1);

  // Click background → next step
  const handleBackgroundClick = () => { nextStep(); };
  const handleSidebarToggle = () => { setShowSidebar((prev) => !prev); closeMenu(); };
  const handleOverviewAction = () => { zoomToFit(); closeMenu(); };
  const handleOverviewFullWidthAction = () => { zoomToFit({ respectSidebar: false }); closeMenu(); };
  const handlePresentToggle = () => { setPresentMode((v) => !v); closeMenu(); };
  const handleResetAction = () => { goStep(0); closeMenu(); };
  const menuOptions = [
    { key: "sidebar", label: `${sidebarVisible ? "Hide" : "Show"} sidebar (S)`, action: handleSidebarToggle },
    { key: "overview", label: "Overview (F)", action: handleOverviewAction },
    { key: "overviewFull", label: "Overview full width (G)", action: handleOverviewFullWidthAction },
    { key: "present", label: `${presentMode ? "Exit present mode" : "Present mode"} (M)`, action: handlePresentToggle },
    { key: "reset", label: "Reset (R)", action: handleResetAction },
  ];

  // Click node: expand & show details (without changing steps)
  const handleNodeClick = (node) => { setFocusedNode(node); if (EXPANSIONS[node.id]) addExpansion(node.id); };

  const openImageViewer = (src, alt = "") => {
    if (!src) return;
    setImageViewer({ open: true, src, alt });
    setImageZoom(1);
  };

  const closeImageViewer = () => {
    setImageViewer({ open: false, src: "", alt: "" });
  };

  const handleSidebarContentClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLImageElement)) return;
    event.preventDefault();
    event.stopPropagation();
    const src = target.getAttribute("src") || target.currentSrc;
    const alt = target.getAttribute("alt") || "";
    openImageViewer(src, alt);
  };

  const handleImageViewerWheel = (event) => {
    if (!imageViewer.open) return;
    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.12 : 0.12;
    setImageZoom((prev) => {
      const next = Math.min(4, Math.max(0.5, prev + delta));
      return Number(next.toFixed(2));
    });
  };

  const handleImageViewerOverlayClick = (event) => {
    if (event.target === event.currentTarget) closeImageViewer();
  };

  const formatRelationType = (type) => {
    if (type === "child") return "Child link";
    if (type === "detail") return "Detail link";
    if (type === "related") return "Related link";
    return type || "Related link";
  };

  const renderSimpleStepControls = (className = "", buttonSize = "w-9 h-9") => (
    <div className={`select-none ${className}`}>
      <button
        onClick={prevStep}
        className={`${buttonSize} rounded-full border flex items-center justify-center`}
        style={{ borderColor: COLORS.dotIdleBorder, color: COLORS.text, background: "transparent" }}
        aria-label="Previous step"
      >
        ←
      </button>
      <div
        className="text-sm font-semibold px-2 py-1 rounded-full border"
        style={{ borderColor: COLORS.dotIdleBorder, color: COLORS.text }}
      >
        {currentStep}/{totalSteps}
      </div>
      <button
        onClick={nextStep}
        className={`${buttonSize} rounded-full border flex items-center justify-center`}
        style={{ borderColor: COLORS.dotIdleBorder, color: COLORS.text, background: "transparent" }}
        aria-label="Next step"
      >
        →
      </button>
    </div>
  );

  const renderSidebarDetails = () => {
    if (!focused) return null;
    const sidebarMarkdown = typeof focused.sidebar === "string" ? focused.sidebar : "";
    const hasEntries = sidebarMarkdown.trim().length > 0;
    const sidebarHtml = hasEntries ? marked.parse(sidebarMarkdown) : "";
    const relationAvailable = relationEntries.length > 0;
    const contentBlock = hasEntries ? (
      <div
        className="markdown-content text-sm leading-relaxed"
        style={{ color: COLORS.text }}
        onClick={handleSidebarContentClick}
        dangerouslySetInnerHTML={{ __html: sidebarHtml }}
      />
    ) : (
      <div className="text-sm" style={{ color: COLORS.subtext }}>
        No sidebar notes for this node yet.
      </div>
    );

    const relationBlock = (
      <div className="space-y-3">
        <div className="text-xs uppercase tracking-[0.35em]" style={{ color: COLORS.subtext }}>
          Relation
        </div>
        {relationAvailable ? (
          <ul className="space-y-3">
            {relationEntries.map((rel) => {
              const directionLabel = rel.direction === "outgoing" ? "From current node" : "To current node";
              const directionBadge = rel.direction === "outgoing" ? "→" : "←";
              return (
                <li
                  key={`relation-${focused.id}-${rel.id}-${rel.direction}-${rel.type}`}
                  className="rounded-lg border px-3 py-2"
                  style={{ borderColor: COLORS.border, background: "rgba(15, 23, 42, 0.55)" }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold" style={{ color: COLORS.text }}>
                        {rel.label}
                      </div>
                      <div className="text-xs uppercase tracking-wide mt-1" style={{ color: COLORS.subtext }}>
                        {directionLabel} · {formatRelationType(rel.type)}
                      </div>
                    </div>
                    <span
                      className="text-xs px-2 py-1 rounded-full border"
                      style={{ borderColor: COLORS.border, color: COLORS.text }}
                    >
                      {directionBadge}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="text-sm" style={{ color: COLORS.subtext }}>
            No related nodes yet.
          </div>
        )}
      </div>
    );

    const renderTabButton = (tabKey, label) => {
      const isActive = mobileSidebarTab === tabKey;
      return (
        <button
          key={tabKey}
          type="button"
          onClick={() => setMobileSidebarTab(tabKey)}
          className="flex-1 rounded-full py-2 text-sm font-semibold transition-all"
          style={{
            background: isActive ? COLORS.text : "transparent",
            color: isActive ? COLORS.bg : COLORS.text,
          }}
        >
          {label}
        </button>
      );
    };

    return (
      <div className="p-4 md:p-8 space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="mb-2">
              <span
                className="inline-flex items-center rounded-full border px-3 py-0.5 text-[0.65rem] uppercase tracking-[0.35em]"
                style={{ borderColor: COLORS.border, color: COLORS.text, background: "rgba(148, 163, 184, 0.15)" }}
              >
                {focused.kind}
              </span>
            </div>
            <div className="text-xl font-semibold">{focused.label}</div>
          </div>
          <button
            onClick={handleCloseSidebar}
            aria-label="Close sidebar"
            className="w-8 h-8 rounded-full border flex items-center justify-center flex-shrink-0"
            style={{ borderColor: COLORS.border, color: COLORS.text, background: COLORS.bg }}
          >
            ×
          </button>
        </div>
        <div className="hidden md:flex flex-col gap-6">
          {contentBlock}
          <div className="pt-4 border-t" style={{ borderColor: COLORS.border }}>
            {relationBlock}
          </div>
        </div>
        <div className="md:hidden flex flex-col gap-4">
          <div
            className="flex items-center gap-2 rounded-full border px-1 py-1"
            style={{ borderColor: COLORS.border, background: "rgba(15, 23, 42, 0.65)" }}
          >
            {renderTabButton("content", "Content")}
            {renderTabButton("relation", "Relation")}
          </div>
          <div className="space-y-4">
            {mobileSidebarTab === "content" ? contentBlock : relationBlock}
          </div>
        </div>
      </div>
    );
  };

  const renderImageViewerOverlay = () => {
    if (!imageViewer.open) return null;
    const zoomPercent = Math.round(imageZoom * 100);
    const caption = imageViewer.alt?.trim();
    return (
      <div
        className="fixed inset-0 z-40 flex flex-col p-4 md:p-8"
        style={{ background: "rgba(2, 6, 23, 0.85)" }}
        onWheel={handleImageViewerWheel}
        onClick={handleImageViewerOverlayClick}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex justify-end">
          <button
            type="button"
            onClick={closeImageViewer}
            aria-label="Close image viewer"
            className="w-10 h-10 rounded-full border flex items-center justify-center text-xl"
            style={{ borderColor: COLORS.border, color: COLORS.text, background: COLORS.panel }}
          >
            ×
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <img
            src={imageViewer.src}
            alt={imageViewer.alt || "Sidebar attachment"}
            className="max-h-full max-w-full object-contain select-none"
            style={{ transform: `scale(${imageZoom})`, transition: "transform 0.1s ease-out" }}
            draggable={false}
          />
        </div>
        <div className="mt-4 text-center space-y-1">
          <div className="text-xs uppercase tracking-[0.35em]" style={{ color: COLORS.subtext }}>
            Scroll to zoom · {zoomPercent}%
          </div>
          {caption && (
            <div className="text-sm font-medium" style={{ color: COLORS.text }}>
              {caption}
            </div>
          )}
        </div>
      </div>
    );
  };

  // —— Drawing ——
  const nodeCanvasObject = (node, ctx, globalScale) => {
    const isRoot = node.id === "q-onono";
    const baseR = 9 + (node.level ?? 0) * 1.4;
    const r = isRoot ? baseR * 2.2 : baseR; // Larger for center
    const fill = isRoot ? COLORS.rootFill : (COLORS[node.kind] || "#94a3b8");

    // glow for root
    if (isRoot) { ctx.save(); ctx.shadowColor = COLORS.rootGlow; ctx.shadowBlur = 28; }

    // Node body
    ctx.beginPath(); ctx.arc(node.x, node.y, r, 0, 2 * Math.PI, false); ctx.fillStyle = fill; ctx.fill();

    if (isRoot) ctx.restore();

    // Highlight: node currently shown in sidebar (focused)
    const isFocused = focused && focused.id === node.id;
    if (isFocused) {
      ctx.save();
      ctx.lineWidth = 2.5 / Math.max(1, globalScale);
      ctx.strokeStyle = COLORS.focusStroke;
      ctx.shadowColor = COLORS.focusGlow;
      ctx.shadowBlur = 18;
      ctx.beginPath(); ctx.arc(node.x, node.y, r + 3 / Math.max(1, globalScale), 0, 2 * Math.PI); ctx.stroke();
      ctx.restore();
    }

    // Labels (larger for center)
    if (!hideNodeLabels) {
      const label = node.label || node.id;
      const fontSize = isRoot ? 18 : 14; // Slightly larger labels overall
      ctx.font = `${fontSize / globalScale}px Inter, system-ui, -apple-system`;
      ctx.textAlign = "center"; ctx.textBaseline = "top"; ctx.fillStyle = COLORS.text;
      ctx.fillText(label, node.x, node.y + r + 3);
    }
  };

  const linkColor = (l) => {
    const rawType = (l.type || "").toString().toLowerCase();
    const childLike = new Set(["child", "reframes", "redefines", "grounds", "context", "asks"]);
    const detailLike = new Set(["detail", "advantage", "contrasts"]);
    if (childLike.has(rawType)) return COLORS.edgeChild;
    if (detailLike.has(rawType)) return COLORS.edgeDetail;
    return COLORS.edgeRelated;
  };
  const linkEndpointId = (endpoint) => (typeof endpoint === "object" && endpoint !== null ? endpoint.id : endpoint);
  const linkCanvasObject = (link, ctx, globalScale) => {
    if (!showRelationLabels) return;
    const rawType = link.type;
    if (!rawType) return;
    const type = String(rawType);
    const normalizedType = type.toLowerCase();
    const accent = RELATION_ACCENT_COLORS[normalizedType] || COLORS.edgeDetail;
    const sourceId = linkEndpointId(link.source);
    const targetId = linkEndpointId(link.target);
    if (!RELATION_LABEL_TARGETS.has(sourceId) && !RELATION_LABEL_TARGETS.has(targetId)) return;
    const start = link.source;
    const end = link.target;
    if (!start || !end || typeof start.x !== "number" || typeof end.x !== "number") return;
    const midX = (start.x + end.x) / 2;
    const midY = (start.y + end.y) / 2;
    const dx = (end.x - start.x);
    const dy = (end.y - start.y);
    const len = Math.hypot(dx, dy) || 1;
    const offset = 18 / Math.max(1, globalScale);
    const normX = (-dy / len) * offset;
    const normY = (dx / len) * offset;
    const labelX = midX + normX;
    const labelY = midY + normY;
    const fontSize = Math.max(11 / Math.max(1, globalScale), 7);
    const paddingX = 6 / Math.max(1, globalScale);
    const paddingY = 3 / Math.max(1, globalScale);
    const label = type.replace(/\s+/g, " ").replace(/-/g, " ").toUpperCase();
    ctx.save();
    ctx.font = `600 ${fontSize}px Inter, system-ui, -apple-system`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const textWidth = ctx.measureText(label).width;
    const boxWidth = textWidth + paddingX * 2;
    const boxHeight = fontSize + paddingY * 2;
    const boxX = labelX - boxWidth / 2;
    const boxY = labelY - boxHeight / 2;

    ctx.lineWidth = 1 / Math.max(1, globalScale);
    ctx.strokeStyle = accent;
    ctx.beginPath();
    ctx.moveTo(midX, midY);
    ctx.lineTo(labelX, labelY);
    ctx.stroke();

    ctx.fillStyle = "rgba(15, 23, 42, 0.7)";
    ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
    ctx.strokeStyle = `rgba(148, 163, 184, 0.55)`;
    ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);

    ctx.fillStyle = accent;
    ctx.fillText(label, labelX, labelY);
    ctx.restore();
  };

  return (
    <div
      className="w-full min-h-screen overflow-hidden"
      style={{
        background: COLORS.bg,
        color: COLORS.text,
        minHeight: "var(--app-viewport-height, 100vh)",
        height: "var(--app-viewport-height, 100vh)",
      }}
    >
      <div className="flex h-full flex-col md:grid md:grid-cols-12">
        {/* Graph area */}
        <div className={`relative z-0 flex-1 min-h-0 w-full ${graphCols}`}>
          <ForceGraph2D
            ref={fgRef}
            graphData={graphData}
            nodeRelSize={6}
            linkColor={linkColor}
            linkCanvasObject={linkCanvasObject}
            linkCanvasObjectMode={() => "after"}
            linkDirectionalArrowLength={8}
            linkDirectionalArrowRelPos={1}
            linkCurvature={(l) => (l.type === "related" ? 0.2 : 0.08)}
            linkLineDash={(l) => (l.type === "related" ? [3, 3] : undefined)}
            cooldownTicks={80}
            backgroundColor={COLORS.bg}
            onEngineStop={() => { if (graphData.nodes.length === 1) zoomCenter(1.35); }}
            nodeCanvasObject={nodeCanvasObject}
            onNodeClick={handleNodeClick}
            onBackgroundClick={handleBackgroundClick}
            enableZoomInteraction={true}
            enablePanInteraction={true}
          />

          {/* Hamburger menu */}
          <div className="fixed top-2 left-2 z-30 md:top-4 md:left-4" ref={menuRef}>
            <button
              className="w-8 h-8 md:w-9 md:h-9 rounded-lg border flex items-center justify-center text-base leading-none"
              style={{ borderColor: COLORS.border, background: COLORS.panel, color: COLORS.text }}
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              ☰
            </button>
            {menuOpen && (
              <div
                className="mt-2 w-48 rounded-md border shadow-lg py-2 text-sm"
                style={{ background: COLORS.panel, borderColor: COLORS.border, color: COLORS.text }}
              >
                {menuOptions.map((item) => (
                  <button
                    key={item.key}
                    onClick={item.action}
                    className="w-full text-left px-4 py-2 hover:opacity-80"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Step indicators */}
          {renderSimpleStepControls("hidden md:flex items-center gap-4 absolute left-3 bottom-3 z-10")}
          {renderSimpleStepControls("flex md:hidden items-center gap-4 fixed top-3 right-3 z-20")}

          {/* Final overlay */}
          <AnimatePresence>
            {finalOverlay && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex items-end justify-center px-6 pb-12 md:pb-20 pointer-events-none z-10"
              >
                <div className="text-center">
                  <div className="text-3xl md:text-5xl font-bold tracking-[0.24em] md:tracking-[0.3em] mb-2">
                    THIS IS ONONO.
                  </div>
                  <div className="text-lg md:text-2xl opacity-80 tracking-[0.24em]">
                    OVERLAPPING NETWORKS OF NESTED ONTOLOGIES
                  </div>
                  <div className="block md:hidden h-10" aria-hidden="true" />
                  <div className="block md:hidden h-10" aria-hidden="true" />
                  <div className="block md:hidden h-10" aria-hidden="true" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {sidebarVisible && (
          <div
            className="hidden md:flex md:col-span-3 md:flex-col h-full min-h-0 z-0"
            style={{ borderLeft: `1px solid ${COLORS.border}`, background: COLORS.panel }}
          >
            <div className="flex-1 overflow-y-auto">{renderSidebarDetails()}</div>
          </div>
        )}

        {sidebarVisible && (
          <div
            className="md:hidden flex-shrink-0 z-10 w-full"
            style={{ borderTop: `1px solid ${COLORS.border}`, background: COLORS.panel, height: "45vh" }}
          >
            <div className="h-full overflow-y-auto">{renderSidebarDetails()}</div>
          </div>
        )}
      </div>
      {renderImageViewerOverlay()}
    </div >
  );
}
